import React, { useEffect, useMemo } from "react";
import { Button, Stack } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Col, Container, Row, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { PageHeader } from "@/CommonElements/PageTitle";
import { Controllers } from "@/CommonElements/Controller";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { LabelValue } from "@/Component/Value";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useSystem } from "@/utils/hooks/useLocation";
import { DATE_TIME_FORMAT } from "@/utils/constants";
import {
  useGetNusukCardsReturnReasonsQuery,
  useGetOneNusukCardsReturnQuery,
  useUpdateNusukCardsReturnMutation,
} from "@/services/nusukCards/nusukCards";
import { handleFormErrors } from "@/utils/helpers";
import {
  mergeReturnRow,
  pickCampaignOrGroupNumber,
  pickNationality,
  pickOrganizer,
  pickServiceCenter,
  unwrapReturnPayload,
} from "./nusukReturnRecord";

const COL_LAYOUT = { colProps: { xs: 12, md: 4 } };
const COL_LAYOUT_WIDE = { colProps: { xs: 12, md: 8 } };

const EditNusukCardsReturn = () => {
  const { t } = useTranslation();
  const { batchId: batchIdParam, returnId } = useParams();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();
  const { getLocalizedText, getLocalizedDate } = useLocalizedText();

  const returnsListLink = `/app/${currentSystem}/nusuk-cards/returns`;

  const { data, isLoading, isError } = useGetOneNusukCardsReturnQuery(returnId);
  const raw = unwrapReturnPayload(data);
  const row = useMemo(() => mergeReturnRow(raw), [raw]);

  const batchId =
    row?.batch_id ??
    row?.nusuk_cards_batch_id ??
    row?.batch?.id ??
    batchIdParam;

  const { data: returnReasons = [], isLoading: reasonsLoading } =
    useGetNusukCardsReturnReasonsQuery();
  const { mutate: updateReturn, isPending } = useUpdateNusukCardsReturnMutation();

  const form = useForm({
    defaultValues: {
      reason_id: undefined,
      representative_name: "",
      notes: "",
    },
  });
  const { control, reset, handleSubmit, watch, formState: { errors } } = form;

  useEffect(() => {
    if (row) {
      reset(      {
        reason_id: row?.reason_id ?? undefined,
        representative_name: row?.representative_name ?? "",  
        notes: row?.notes ?? "",});
    }
  }, [reset, row]);

  const reasonOptions = useMemo(
    () =>
      (returnReasons || [])
        .map((r) => {
          const id = r.id ?? r.value;
          if (id == null || id === "") return null;
          const label = getLocalizedText(r.name_ar, r.name_en) ;
          return { value: id, label };
        })
        .filter(Boolean),
    [returnReasons, getLocalizedText]
  );

  const organizerLabel = pickOrganizer(row, getLocalizedText);

  const onSubmit = (data) => {
    if (!returnId || !batchId) {
      toast.error(t("SOMETHING_WENT_WRONG"));
      return;
    }

    const trimmedName = String(data.representative_name || "").trim();
    const trimmedNotes = String(data.notes || "").trim();
    const reasonIdNum = Number(data.reason_id);
    const payload = {
      reason_id: Number.isFinite(reasonIdNum) ? reasonIdNum : data.reason_id,
      representative_name: trimmedName,
      notes: trimmedNotes.length ? trimmedNotes : null,
    };

    updateReturn(
      { batchId, returnId, payload },
      {
        onSuccess: () => {
          toast.success(t("CHANGES_SAVED_SUCCESSFULLY"));
          navigate(returnsListLink);
          reset();
        },
        onError: (error) => {
          const handled = handleFormErrors(
            error,
            form.setError,
            t,
            "SOMETHING_WENT_WRONG"
          );
          if (!handled) {
            toast.error(error?.response?.data?.message || t("SOMETHING_WENT_WRONG"));
          }
        },
      }
    );
  };

  const formControllers = useMemo(() => {
    const ctrls = [
      {
        labelProps: {
          children: t("NUSUK_RETURN_REASON_COL"),
          className: "fw-bold required",
        },
        controllerProps: {
          name: "reason_id",
          rules: {
            validate: (v) =>
              v != null && v !== "" ? true : t("FIELD_REQUIRED"),
          },
          render: ({ field }) => (
            <AntSelect
              className="w-100"
              showSearch
              optionFilterProp="label"
              loading={reasonsLoading}
              options={reasonOptions}
              value={
                field.value != null && field.value !== ""
                  ? field.value
                  : undefined
              }
              onChange={(v) => field.onChange(v ?? undefined)}
              onBlur={field.onBlur}
              placeholder={t("NUSUK_RETURN_REASON_PLACEHOLDER")}
              disabled={isPending}
              status={errors.reason_id ? "error" : undefined}
            />
          ),
        },
        layout: COL_LAYOUT,
      },
      {
        labelProps: {
          children: t("NUSUK_COMPLETE_CASE_RECIPIENT"),
          className: "fw-bold required",
        },
        controllerProps: {
          name: "representative_name",
          rules: {
            required: t("FIELD_REQUIRED"),
            maxLength: { value: 30, message: t("NUSUK_MAX_CHARS", { max: 30 }) },
          },
          render: ({ field }) => (
            <AntInput
              {...field}
              defaultValue={row?.representative_name ?? ""}
              maxLength={30}
              placeholder={t("NUSUK_REPRESENTATIVE_NAME_PLACEHOLDER")}
              disabled={isPending}
              status={errors.representative_name ? "error" : undefined}
            />
          ),
        },
        layout: COL_LAYOUT,
      },
      {
        labelProps: { children: t("NOTES"), className: "fw-bold" },
        controllerProps: {
          name: "notes",
          render: ({ field }) => (
            <AntInput
              {...field}
              defaultValue={row?.notes ?? ""}
              placeholder={t("NOTES")}
              disabled={isPending}
              status={errors.notes ? "error" : undefined}
            />
          ),
        },
        layout: COL_LAYOUT_WIDE,
      },
    ];

    return ctrls.map((c) => ({
      ...c,
      controllerProps: { ...c.controllerProps, control, errors },
    }));
  }, [t, reasonsLoading, reasonOptions, isPending, errors, row?.representative_name, row?.notes, control]);

  if (isLoading) {
    return (
      <Stack gap={2}>
        <PageHeader
          title={t("NUSUK_CARDS_RETURN_ENTRY_SCREEN")}
          backTo={returnsListLink}
          sticky={false}
        />
        <Container fluid className="d-flex justify-content-center py-5">
          <Spinner />
        </Container>
      </Stack>
    );
  }

  if (isError || !row) {
    return (
      <Stack gap={2}>
        <PageHeader
          title={t("NUSUK_CARDS_RETURN_ENTRY_SCREEN")}
          backTo={returnsListLink}
          sticky={false}
        />
        <Container fluid>
          <p className="text-muted mb-3">{t("NO_DATA")}</p>
          <Link to={returnsListLink} className="btn btn-primary">
            {t("BACK")}
          </Link>
        </Container>
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      <PageHeader
        title={t("NUSUK_CARDS_RETURN_ENTRY_SCREEN")}
        backTo={returnsListLink}
        sticky={false}
      />

      <Container fluid>
        <div className="d-flex flex-wrap gap-4 mb-3 text-muted small">
          <span>
            <span className="fw-bold">{t("NUSUK_RETURN_CREATOR")}:</span>{" "}
            <span className="text-danger">{row?.added_by_name ?? "-"}</span>
          </span>
          { row?.added_at ? (
            <span>
              <span className="fw-bold">{t("DATE")}:</span>{" "}
              {getLocalizedDate( row?.added_at, DATE_TIME_FORMAT)}
            </span>
          ) : null}
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="card border rounded-3 mb-4">
            <div className="card-body py-3 px-4">
              <Row className="g-3 mb-3">
                <Col xs={12} md={4}>
                  <LabelValue
                    label={t("NUSUK_BATCH_NUMBER")}
                    value={row?.batch_no ?? "-"}
                    direction="vertical"
                  />
                </Col>
                <Col xs={12} md={4}>
                  <LabelValue
                    label={t("CAMPAIGN_OR_GROUP_NUMBER")}
                    value={pickCampaignOrGroupNumber(row)}
                    direction="vertical"
                  />
                </Col>
                <Col xs={12} md={4}>
                  <LabelValue
                    label={t("CAMPAIGN_OR_GROUP_NAME")}
                    value={row?.batch?.entity === "campaign" ? row?.batch?.campaign_name : row?.batch?.group_name}
                    direction="vertical"
                  />
                </Col>

                <Col xs={12} md={4}>
                  <LabelValue
                    label={t("SERVICE_CENTER")}
                    value={pickServiceCenter(row, getLocalizedText)}
                    direction="vertical"
                  />
                </Col>
                <Col xs={12} md={4}>
                  <LabelValue
                    label={t("ORGANIZER")}
                    value={organizerLabel}
                    direction="vertical"
                  />
                </Col>
                <Col xs={12} md={4}>
                  <LabelValue
                    label={t("NATIONALITY")}
                    value={pickNationality(row, getLocalizedText)}
                    direction="vertical"
                  />
                </Col>

                <Col xs={12} md={4}>
                  <LabelValue
                    label={t("GROUP_NAME")}
                    value={row?.group_name ?? "-"}
                    direction="vertical"
                  />
                </Col>
                <Col xs={12} md={4}>
                  <LabelValue
                    label={t("NUSUK_CARDS_COUNT")}
                    value={row?.card_no ?? "-"}
                    direction="vertical"
                  />
                </Col>
              </Row>

              <Controllers controllers={formControllers} watch={watch} />
            </div>
          </div>

          <ActionsFooter>
            <Button
              className="btn btn-dark"
              type="submit"
              disabled={isPending || reasonsLoading}
            >
              {isPending ? (
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                  role="status"
                />
              ) : null}
              {t("SAVE")}
            </Button>
            <Link
              to={returnsListLink}
              className="btn btn-danger"
              style={{ pointerEvents: isPending ? "none" : "auto" }}
            >
              {t("CANCEL")}
            </Link>
          </ActionsFooter>
        </form>
      </Container>
    </Stack>
  );
};

export default EditNusukCardsReturn;
